import {
  ActionIcon,
  Badge,
  Button,
  CopyButton,
  Divider,
  Modal,
  Table,
  Text,
  Textarea,
  TextInput,
  TextProps,
  Tooltip,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { Container } from "@mui/material";
import { GridRenderCellParams } from "@mui/x-data-grid";
import {
  IconChevronRight,
  IconCircleFilled,
  IconEye,
  IconPlus,
  IconReload,
} from "@tabler/icons-react";
import dayjs from "dayjs";
import React, { useState } from "react";
import { Link, useParams } from "react-router-dom";
import DatagridDisplay from "../../Components/DatagridDisplay";
import { Colors, HttpSuccessCodes, HttpSuccessCodesObj } from "../../Lib/data";
import { Endpoints } from "../../Lib/endpoints";
import { NonPaginatedResponse, PaginatedResponse } from "../../Lib/responses";
import { AssessmentProps, LessonProps, PaginationProps } from "../../Lib/types";
import {
  DefaultErrorNotification,
  DefaultSuccessNotification,
  getEllipsisFromString,
} from "../../Lib/methods";
import { PerformRequest, usePerformRequest } from "../../Lib/usePerformRequest";
import { useForm } from "@mantine/form";
import { generateRandomString } from "../../Lib/methods";

const Assessments: React.FC = () => {
  const { lessonID } = useParams();

  const [pagination, setPagination] = useState<PaginationProps>({
    page: 1,
    limit: 10,
  });
  const {
    data: lesson,
    isLoading: isLoadingLesson,
    reloadData: reloadLesson,
  } = usePerformRequest<NonPaginatedResponse<LessonProps>>({
    route: Endpoints.GetSingleLesson(lessonID as string).route,
    method: Endpoints.GetSingleLesson(lessonID as string).method,
  });
  const {
    data: assessments,
    isLoading: isLoadingAssessments,
    reloadData: reloadAssessments,
  } = usePerformRequest<PaginatedResponse<AssessmentProps[]>>({
    route: Endpoints.GetSingleLessonAssessments(
      lessonID as string,
      pagination.page,
      pagination.limit
    ).route,
    method: Endpoints.GetSingleLessonAssessments(
      lessonID as string,
      pagination.page,
      pagination.limit
    ).method,
  });

  const modalForm = useForm({
    initialValues: {
      title: "",
    },
    validate: {
      title: (value) =>
        value.length < 3 ? "Title must be at least 3 characters" : null,
    },
  });

  const [modalFormAction, setModalFormAction] = useState<
    "create" | "view" | "update"
  >("create");
  const [viewingAssessment, setViewingAssessment] = useState<AssessmentProps>();

  const [
    isAssessmentModalOpened,
    { open: OpenAssessmentModal, close: CloseAssessmentModal },
  ] = useDisclosure(false);

  const [isCreatingOrUpdatingAssessment, setCreatingOrUpdatingAssessment] =
    useState(false);

  const CreateAssessment = async () => {
    const { hasErrors } = modalForm.validate();
    if (!hasErrors) {
      setCreatingOrUpdatingAssessment(true);
      const { title } = modalForm.values;
      const r = await PerformRequest({
        route: Endpoints.GetCreateAssessment(lessonID as string).route,
        method: Endpoints.GetCreateAssessment(lessonID as string).method,
        data: {
          title,
        },
        callback: () => {
          setCreatingOrUpdatingAssessment(false);
        },
      });
      setCreatingOrUpdatingAssessment(false);

      if (r && r.status === HttpSuccessCodesObj.Created) {
        CloseAssessmentModal();
        modalForm.reset();
        setModalFormAction("create");
        DefaultSuccessNotification("Assessment created successfully");
        reloadAssessments();
      } else {
        DefaultErrorNotification(r?.message ?? "An error occurred");
      }
    }
  };
  const [isTogglingAssessmentStatus, setTogglingAssessmentStatus] =
    useState(false);
  const ToggleAssessmentStatus = async () => {
    setTogglingAssessmentStatus(true);
    const r = await PerformRequest({
      route: Endpoints.GetToggleAssessmentStatus(
        viewingAssessment?.id as string
      ).route,
      method: Endpoints.GetToggleAssessmentStatus(
        viewingAssessment?.id as string
      ).method,
      callback: () => {
        setTogglingAssessmentStatus(false);
      },
      data: {
        isActive: !viewingAssessment?.isActive,
      },
    });
    setTogglingAssessmentStatus(false);
    if (r && r.status === HttpSuccessCodesObj.Success) {
      DefaultSuccessNotification("Assessment status updated successfully");
      reloadAssessments();
      setViewingAssessment({
        ...(viewingAssessment as AssessmentProps),
        isActive: !viewingAssessment?.isActive,
      });
    } else {
      DefaultErrorNotification(r?.message ?? "An error occurred");
    }
  };
  const textProps: TextProps = {
    fz: 13,
    className: "table-value",
  };
  return (
    <Container maxWidth="lg">
      <div className="assessments-container">
        <div className="flex-row align-center justify-between width-100">
          <Text fw={500} fz={24} c={Colors.PurplePrimary}>
            Assessments
          </Text>
          <div className="flex-row align-center">
            <ActionIcon
              size="lg"
              color={Colors.PurplePrimary}
              variant="filled"
              onClick={() => {
                setModalFormAction("create");
                OpenAssessmentModal();
              }}
              disabled={isLoadingAssessments}
            >
              <IconPlus size={18} />
            </ActionIcon>
            &nbsp; &nbsp;
            <ActionIcon
              size="lg"
              color={Colors.PurplePrimary}
              variant="outline"
              onClick={reloadAssessments}
              loading={isLoadingAssessments}
            >
              <IconReload size={18} />
            </ActionIcon>
          </div>
        </div>
        <Divider my={20} />
        <div className="flex-row align-center">
          <Badge
            ml={4}
            mb={10}
            fw={500}
            fz={14}
            size="lg"
            color={Colors.PurplePrimary}
          >
            {lesson?.data.title}
          </Badge>
        </div>
        <div className="flex-row align-center">
          <Link to={`/d/course/${lesson?.data?.courseID}`}>
            <Button
              size="compact-xs"
              variant="subtle"
              color={Colors.PurplePrimary}
            >
              Back to Course
            </Button>
          </Link>
          <Link to={`/d/lesson/materials/${lessonID}`}>
            <Button
              size="compact-xs"
              variant="subtle"
              color={Colors.PurplePrimary}
            >
              Lesson Materials
            </Button>
          </Link>
        </div>
        <Divider my={20} />
        <DatagridDisplay
          rows={assessments?.data ?? []}
          isLoading={isLoadingAssessments}
          columns={[
            {
              field: "createdAt",
              flex: 1,
              headerName: "Creation Date",
              renderCell: (params: GridRenderCellParams<AssessmentProps>) => (
                <div className="flex-row flex-1 align-center">
                  <Text {...textProps}>
                    {dayjs(params.row.createdAt).format("ddd, MMMM DD YYYY")}
                  </Text>
                </div>
              ),
            },
            {
              field: "title",
              flex: 1,
              headerName: "Title",
              renderCell: (params: GridRenderCellParams<AssessmentProps>) => (
                <div className="flex-row flex-1 align-center">
                  <Text {...textProps}>{params.row.title}</Text>
                </div>
              ),
            },
            {
              field: "isActive",
              flex: 1,
              headerName: "Status",
              renderCell: (params: GridRenderCellParams<AssessmentProps>) => (
                <div className="flex-row flex-1 align-center">
                  <Text
                    {...textProps}
                    c={
                      params.row.isActive
                        ? Colors.GreenPrimary
                        : Colors.RedPrimary
                    }
                  >
                    {params.row.isActive ? "Active" : "Disabled"}
                  </Text>
                </div>
              ),
            },

            {
              headerName: "Actions",
              field: "id",
              width: 140,

              renderCell: (params: GridRenderCellParams<AssessmentProps>) => (
                <div className="flex-row flex-1 align-center">
                  <div className="flex-row align-center">
                    <ActionIcon
                      variant="subtle"
                      onClick={() => {
                        console.log(params.row);
                        OpenAssessmentModal();
                        setModalFormAction("view");
                        setViewingAssessment(params.row);
                      }}
                    >
                      <IconEye size={18} />
                    </ActionIcon>
                  </div>
                </div>
              ),
            },
          ]}
        />
      </div>
      <Modal
        title={
          modalFormAction === "create"
            ? "Create Assessment"
            : modalFormAction === "view"
            ? "View Assessment"
            : "Update Assessment"
        }
        opened={isAssessmentModalOpened}
        onClose={() => {
          CloseAssessmentModal();
          setModalFormAction("create");
          setViewingAssessment(undefined);
          modalForm.reset();
        }}
        size="lg"
        centered
      >
        {modalFormAction === "view" ? (
          <>
            {viewingAssessment && (
              <>
                <Table
                  mt={20}
                  variant="vertical"
                  layout="fixed"
                  withTableBorder
                >
                  <Table.Thead>
                    <Table.Tr key={generateRandomString(12)}>
                      <Table.Th>Title</Table.Th>
                      <Table.Td>{viewingAssessment.title}</Table.Td>
                    </Table.Tr>

                    <Table.Tr key={generateRandomString(12)}>
                      <Table.Th>Creation Date</Table.Th>
                      <Table.Td>
                        {dayjs(viewingAssessment.createdAt).format(
                          "ddd, MMMM DD YYYY"
                        )}
                      </Table.Td>
                    </Table.Tr>
                    <Table.Tr key={generateRandomString(12)}>
                      <Table.Th>Status</Table.Th>
                      <Table.Td>
                        <Badge
                          color={
                            viewingAssessment?.isActive
                              ? Colors.GreenPrimary
                              : Colors.RedPrimary
                          }
                        >
                          {viewingAssessment?.isActive ? "Active" : "Disabled"}
                        </Badge>
                      </Table.Td>
                    </Table.Tr>
                    <Table.Tr key={generateRandomString(12)}>
                      <Table.Th>Questions Count</Table.Th>
                      <Table.Td>
                        {viewingAssessment?.questions?.length ?? 0}
                      </Table.Td>
                    </Table.Tr>
                  </Table.Thead>
                </Table>
                <div className="flex-row width-100 align-center">
                  <Link to={`/d/assessment/${viewingAssessment.id}`}>
                    <Button mt={20} w={120} color={Colors.PurplePrimary}>
                      Details
                    </Button>
                  </Link>
                  <Tooltip
                    label={
                      viewingAssessment?.isActive
                        ? "Disabling this assessment will prevent students from accessing it"
                        : "Enabling this assessment will allow students to access it"
                    }
                  >
                    <Button
                      mt={20}
                      w={120}
                      ml={10}
                      color={
                        viewingAssessment?.isActive
                          ? Colors.RedPrimary
                          : Colors.GreenPrimary
                      }
                      onClick={() => {
                        ToggleAssessmentStatus();
                      }}
                      loading={isTogglingAssessmentStatus}
                    >
                      {viewingAssessment?.isActive ? "Disable" : "Enable"}
                    </Button>
                  </Tooltip>
                </div>
              </>
            )}
          </>
        ) : (
          <>
            <TextInput
              label="Title"
              placeholder="Enter title"
              {...modalForm.getInputProps("title")}
            />

            <Button
              mt={10}
              color={Colors.PurplePrimary}
              loading={isLoadingAssessments || isCreatingOrUpdatingAssessment}
              onClick={() => {
                if (modalFormAction === "create") {
                  CreateAssessment();
                } else {
                  //   UpdateAssessment();
                }
              }}
            >
              Submit
            </Button>
          </>
        )}
      </Modal>
    </Container>
  );
};

export default Assessments;
